/**
 * IF Pair Card — both legs side-by-side with the binding-leg highlighted
 * and pair-level derived numbers (per-trade SL, daily target, days-to-
 * withdrawal) above the comparison.
 */

import { type ReactNode } from "react";
import { motion } from "motion/react";
import { Link } from "react-router-dom";
import type { IfPairSummary, IfAccountSummary } from "@/lib/api/accounts-matcher";
import { CoolIcon } from "@/components/ui/cool-icon";
import { FirmAvatar } from "@/components/firm-logo";
import { firmLogoUrl } from "@/lib/firm-logos";
import { cn } from "@/lib/utils";

export function IfPairCard({
  pair,
  index,
}: {
  pair: IfPairSummary;
  index: number;
}): ReactNode {
  const { derived } = pair;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1],
        delay: Math.min(index, 8) * 0.04,
      }}
      className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-4"
    >
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CoolIcon name="Link_Horizontal" size={14} className="text-accent" />
          <Link
            to={`/pairs/${pair.pairSlug}`}
            className="font-mono text-xs uppercase tracking-wider text-foreground hover:text-accent"
          >
            {pair.pairSlug}
          </Link>
        </div>
        <span className="rounded-full border border-accent/30 bg-accent/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-accent">
          {derived.bindingLeg === "tie"
            ? "Both Legs Tie"
            : `Leg ${derived.bindingLeg} Binds`}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <PairTile label="Per-Trade SL" value={`$${fmt(derived.perTradeSlMaxUsd)}`} accent />
        <PairTile
          label="Daily Target"
          value={
            Number.isFinite(derived.dailyTargetUsd)
              ? `$${fmt(derived.dailyTargetUsd)}`
              : "—"
          }
        />
        <PairTile
          label="Days To Withdraw"
          value={derived.daysToFirstDualWithdrawal > 0 ? `${derived.daysToFirstDualWithdrawal}` : "—"}
        />
        <PairTile label="Lifetime Hits" value={`${derived.lifetimeSlHitBudget}`} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <LegPanel
          label="Leg A"
          account={pair.legA}
          isBinding={derived.bindingLeg === "A"}
        />
        <LegPanel
          label="Leg B"
          account={pair.legB}
          isBinding={derived.bindingLeg === "B"}
        />
      </div>

      {derived.notes.length > 0 && (
        <ul className="flex flex-col gap-1.5 border-t border-border/40 pt-3 text-[11px] text-muted-foreground">
          {derived.notes.map((note, i) => (
            <li key={i} className="flex items-start gap-2">
              <CoolIcon name="Info" size={11} className="mt-0.5 shrink-0 text-accent" />
              <span>{note}</span>
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
}

function LegPanel({
  label,
  account,
  isBinding,
}: {
  label: string;
  account: IfAccountSummary;
  isBinding: boolean;
}): ReactNode {
  const logo = firmLogoUrl(account.firmId) ?? account.firmLogo ?? "";
  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-card/60 p-3",
        isBinding ? "border-accent/60 ring-1 ring-accent/30" : "border-border/40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <FirmAvatar src={logo} name={account.firmName} size="sm" />
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-foreground">
              {account.firmName}
            </p>
            <p className="truncate font-mono text-[10px] text-muted-foreground">
              {account.accountLabel ?? account.login ?? account.slug}
            </p>
          </div>
        </div>
        <span className="rounded-md border border-border/40 bg-background px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-1 text-[10px] text-muted-foreground">
        <RuleEntry k="Size" v={`$${account.risk.startingBalance.toLocaleString()}`} />
        <RuleEntry k="Floating" v={pctOrNone(account.risk.maxFloatingPct)} />
        <RuleEntry k="Daily" v={pct(account.risk.dailyDdPct)} />
        <RuleEntry k="Lifetime" v={pct(account.risk.lifetimeDdPct)} />
        <RuleEntry k="Consist." v={pctOrNone(account.risk.consistencyPct)} />
        <RuleEntry
          k="Per-Trade"
          v={
            account.derived.perTradeSlMaxUsd != null
              ? `$${fmt(account.derived.perTradeSlMaxUsd)}`
              : "—"
          }
        />
      </dl>
    </div>
  );
}

function PairTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border/40 bg-background/30 p-3">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          "font-mono text-xl font-semibold tabular-nums",
          accent ? "text-accent" : "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function RuleEntry({ k, v }: { k: string; v: string }): ReactNode {
  return (
    <div className="flex items-center justify-between border-b border-border/20 py-0.5 last:border-b-0">
      <span>{k}</span>
      <span className="font-mono text-foreground/80">{v}</span>
    </div>
  );
}

function pct(v: number): string {
  return `${(v * 100).toFixed(v < 0.01 ? 2 : 1).replace(/\.?0+$/, "")}%`;
}

function pctOrNone(v: number | null): string {
  return v == null ? "None" : pct(v);
}

function fmt(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
